import React, { useRef } from "react";
import { TimeLinePropsModel } from "@models/strapi-components/timeline";
import Title from "@components/common/Title";
import { motion, Variants } from "framer-motion";
import ImageRenderer from "@components/common/Image";
import { twMerge } from "tailwind-merge";
import VizWrapper from "@components/common/Base/VizWrapper";
import useIcons from "@hooks/use-source-icons";

const variants: Variants = {
  offscreenLeft: {
    opacity: "0%",
    x: "-20px",
  },
  offscreenRight: {
    opacity: "0%",
    x: "20px",
  },
  onscreen: {
    opacity: "100%",
    x: "0px",
  },
};

const viewport = {
  once: true,
  amount: 0.8,
};

export const TimeLine: React.FunctionComponent<TimeLinePropsModel> = ({
  componentData,
}) => {
  const { title, timeLineFeature } = componentData;
  const ref = useRef(null);
  const { getFromReactIcons } = useIcons();
  return (
    <VizWrapper
      className="flex flex-col justify-center items-center bg-white px-8"
      ref={ref}
    >
      <Title
        titleText={title}
        variant="h2"
        className="text-center lg:max-w-auto lg:max-xl-[60%] xl:max-w-[80%]"
      />
      <div className="relative max-w-[1440px] w-[83.25%] pt-[107.5px] space-y-[70px] hidden lg:block">
        {timeLineFeature.map((feature, index) => {
          const { title, subTitle, id, image, icon } = feature;
          const Icon = getFromReactIcons(icon ?? "");

          const reversed = index % 2 !== 0;
          return (
            <div
              key={id}
              className={twMerge(
                "flex justify-between",
                reversed && "flex-row-reverse"
              )}
            >
              <motion.div
                className={`w-1/2 relative flex flex-col justify-center ${
                  reversed ? "pl-[78px]" : "pr-[78px]"
                }`}
                viewport={viewport}
                initial={reversed ? "offscreenLeft" : "offscreenRight"}
                whileInView="onscreen"
                variants={variants}
              >
                <div
                  className={`w-full relative ${
                    reversed ? "left-indicator" : "right-indicator"
                  }`}
                >
                  <div className="bg-primary-blue-20 w-[60px] aspect-square grid place-content-center rounded-full text-primary-blue-32 text-2xl font-bold">
                    {Icon ? <Icon /> : index + 1}
                  </div>
                </div>
                <div className="max-w-[357px] mt-6">
                  <Title titleText={title} variant="h3" />
                  {subTitle && (
                    <Title titleText={subTitle} variant="paragraph" />
                  )}
                </div>
              </motion.div>
              <motion.div
                className={`w-1/2 ${reversed ? "pr-[78px]" : "pl-[78px]"}`}
                variants={variants}
                initial={reversed ? "offscreenRight" : "offscreenLeft"}
                whileInView="onscreen"
                viewport={viewport}
              >
                <ImageRenderer image={image} alt="Image" />
              </motion.div>
            </div>
          );
        })}
        <div className="w-[2px] bg-grey-15 h-full absolute top-0 left-1/2 -translate-x-1/2">
          <div className="relative w-full h-full transparent">
            <div className="bg-primary-orange-50 w-full absolute top-0 left-0 right-0 transform origin-[0%]" />
          </div>
        </div>
      </div>
      <div className="relative px-8 gap-8 lg:hidden flex flex-col items-center">
        {timeLineFeature?.map((feature) => {
          const { title, subTitle, id, image } = feature;
          return (
            <div key={id} className="flex flex-col items-center">
              <Title
                titleText={title}
                variant="h3"
                className={twMerge(
                  "text-center sm:max-w-[60%] sm:mx-auto mb-8",
                  subTitle && "mb-4"
                )}
              />
              {subTitle && (
                <Title
                  variant="paragraph"
                  titleText={subTitle}
                  className="text-center sm:max-w-[80%] sm:mx-auto mb-8"
                />
              )}
              <ImageRenderer image={image} alt="Image" />
            </div>
          );
        })}
      </div>
    </VizWrapper>
  );
};
